// pages/ManageAdmins.tsx — FINAL RTP FULLY INTEGRATED VERSION
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';
import { useConfirmModal } from '../utils/modal';
import ConfirmModal from '../../shared/components/ConfirmModal';
import { toast } from 'react-hot-toast';

interface Admin {
  id?: string;
  login_id?: string;
  email?: string;
  name: string;
  phone?: string;
  is_verified: boolean;
  banned: boolean;
  is_super_admin?: boolean;
  wallet_balance?: number;
}

interface SearchForm {
  search: string;
}

export default function ManageAdmins() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const { token, fetchAdmins, deleteAdmin, error, setError, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { register, watch } = useForm<SearchForm>({ defaultValues: { search: '' } });
  const search = watch('search', '');

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchAdmins()
        .then((data: Admin[]) => {
          setAdmins((data || []) as any[]);
          setLoading(false);
        })
        .catch((err: Error) => {
          setError(`Failed to fetch admins: ${err.message}`);
          setLoading(false);
        });
    }
  }, [token, fetchAdmins, setError]);

  useEffect(() => {
    if (!socket) return;

    socket.on('admin-updated', () => {
      toast('Admin list updated in real-time');
      // FIX: was missing closing parenthesis — corrected now
      fetchAdmins().then(data => setAdmins((data || []) as any[]));
    });

    // FIX: data type includes both email and login_id as optional
    socket.on('admin-deleted', (data: { email?: string; login_id?: string }) => {
      toast.success('Admin deleted successfully');
      setAdmins(prev =>
        prev.filter(a =>
          !(data.email && a.email === data.email) &&
          !(data.login_id && a.login_id === data.login_id)
        )
      );
    });

    return () => {
      socket.off('admin-updated');
      socket.off('admin-deleted');
    };
  }, [socket, fetchAdmins]);

  const filtered = admins.filter(a =>
    (a.name?.toLowerCase().includes(search.toLowerCase()) || false) ||
    ((a.login_id || a.name || '').toLowerCase().includes(search.toLowerCase()) || false)
  );

  const handleDelete = (admin: Admin) => {
    if (admin.is_super_admin) {
      toast.error('Super admins cannot be deleted');
      return;
    }
    showConfirmModal(
      `Delete admin ${admin.name}? This will revoke all RTP control access. This cannot be undone.`,
      async () => {
        try {
          setAdmins(prev => prev.filter(a => a.id !== admin.id));
          await deleteAdmin(admin.login_id || admin.email || '');
          toast.success(`Admin deleted: ${admin.login_id || admin.name}`);
        } catch (err: any) {
          setAdmins(prev => [...prev, admin]);
          setError(`Delete failed: ${err.message}`);
          toast.error(`Delete failed: ${err.message}`);
        }
      }
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Manage Admins</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">✕</button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search by name or login ID..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('search')}
            aria-label="Search admins"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-indigo-600" size={48} />
            <span className="ml-4 text-xl text-gray-700">Loading admins...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            No admins found {search ? `matching "${search}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-4 text-left font-semibold text-gray-700">Name</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Login ID</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Role</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Balance</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Verified</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Status</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id || a.login_id || a.name} className="border-b hover:bg-gray-50 transition">
                    <td className="p-4 font-medium" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.name || 'N/A') }} />
                    <td className="p-4 font-mono font-bold text-indigo-600">{a.login_id || '—'}</td>
                    <td className="p-4">
                      {a.is_super_admin ? (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          👑 Super Admin
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Admin
                        </span>
                      )}
                    </td>
                    <td className="p-4 font-semibold text-emerald-700">
                      {a.is_super_admin ? '-' : `${Number(a.wallet_balance || 0).toLocaleString()} credits`}
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        a.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {a.is_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        a.banned ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {a.banned ? 'Banned' : 'Active'}
                      </span>
                    </td>
                    <td className="p-4">
                      <button
                        className={`px-4 py-2 text-white text-sm rounded-lg transition shadow-sm ${
                          a.is_super_admin
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                        onClick={() => handleDelete(a)}
                        disabled={a.is_super_admin === true}
                        aria-label={`Delete admin ${a.login_id || a.name}`}
                        title={a.is_super_admin ? 'Super admins cannot be deleted' : 'Delete admin'}
                      >
                        {a.is_super_admin ? '🔒 Protected' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isOpen}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
